import { ref } from 'vue'
import { createLayer } from '@/services/map/LayerService'

export function useMapLayers({ triggerUpdate }) {
  // Layer references
  const layers = ref({
    base: null,
    info: null,
    overlays: null,
  })

  const activeBaseLayer = ref(null)
  const overlays = ref([])

  /**
   * Initialize layer groups
   */
  const initLayerGroups = () => {
    layers.value = {
      base: createLayer({
        type: 'Group',
        layers: [],
      }),
      info: createLayer({
        type: 'Group',
        layers: [],
      }),
      overlays: createLayer({
        type: 'Group',
        layers: [],
      }),
    }

    return layers.value
  }

  /**
   * Create base layers from configuration
   */
  const createBaseLayers = (
    layersConf,
    activeBaseLayerId,
    minZoom,
    maxZoom,
  ) => {
    if (!layersConf || !layersConf.length) {
      return
    }

    if (!activeBaseLayerId) {
      activeBaseLayerId = layersConf[0].id
    }

    layersConf.forEach(layerConf => {
      layerConf.visible = layerConf.id === activeBaseLayerId
      layerConf.zIndex = 0

      if (!('minZoom' in layerConf)) {
        layerConf.minZoom = minZoom
      }

      if (!('maxZoom' in layerConf)) {
        layerConf.maxZoom = maxZoom
      }

      const layer = createLayer(layerConf)
      addLayerTo('base', layer)

      if (layerConf.visible) {
        activeBaseLayer.value = layer
      }
    })
  }

  /**
   * Create layers for a specific group
   */
  const createLayers = (group, layersConf, minZoom, maxZoom) => {
    if (!layersConf || !layersConf.length) {
      return
    }

    layersConf.forEach(layerConf => {
      if (!('minZoom' in layerConf)) {
        layerConf.minZoom = minZoom
      }

      if (!('maxZoom' in layerConf)) {
        layerConf.maxZoom = maxZoom
      }

      addLayerTo(group, createLayer(layerConf))
    })

    triggerUpdate()
  }

  /**
   * Create overlay layer
   */
  const createOverlay = (overlay, minZoom, maxZoom) => {
    if (!overlay) {
      return
    }

    createLayers('overlays', [overlay], minZoom, maxZoom)
    overlays.value.push(overlay)
  }

  /**
   * Add a layer to a group
   */
  const addLayerTo = (group, layer) => {
    if (!layers.value[group]) return

    layers.value[group].getLayers().push(layer)
    triggerUpdate()
  }

  /**
   * Get a layer by ID from a group
   */
  const getLayer = (group, id = null) => {
    if (group in layers.value && id) {
      return layers.value[group]
        .getLayers()
        .getArray()
        .find(layer => {
          return layer.get('id') === id
        })
    }
    return undefined
  }

  /**
   * Change the active base layer
   */
  const changeBaseLayer = id => {
    const layerArray = layers.value.base
      .getLayers()
      .getArray()
      .filter(layer => layer.get('id') === id)

    if (layerArray.length === 1) {
      layers.value.base.getLayers().forEach(layer => {
        layer.setVisible(false)
      })

      layerArray[0].setVisible(true)
      activeBaseLayer.value = layerArray[0]
      triggerUpdate()
    }
  }

  /**
   * Get all layer groups as an array
   */
  const getLayerGroups = () => {
    return Object.keys(layers.value).map(i => layers.value[i])
  }

  return {
    layers,
    activeBaseLayer,
    overlays,
    initLayerGroups,
    createBaseLayers,
    createLayers,
    createOverlay,
    addLayerTo,
    getLayer,
    changeBaseLayer,
    getLayerGroups,
  }
}
